/**
 * Settings panel for warehouse dimensions configuration.
 * Vises som egen fane («Innstillinger») i venstre sidepanel.
 */

import React, { useRef, useState } from 'react';
import { Upload, Trash2, Loader2, Ruler } from 'lucide-react';
import { useLayoutEditor } from '../contexts/LayoutEditorContext';
import { useTranslation } from '../../../../../contexts/I18nProvider';

const MAX_BACKGROUND_BYTES = 5 * 1024 * 1024; // matcher backend-grensen (5 MB)

const SettingsPanel: React.FC = () => {
  const { state, dispatch, backgroundUrl, uploadBackground, removeBackground } = useLayoutEditor();
  const { t } = useTranslation();
  const { layout } = state;
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [backgroundBusy, setBackgroundBusy] = useState(false);

  if (!layout) return null;

  const handleBackgroundFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // tillat re-valg av samme fil
    if (!file) return;

    if (file.size > MAX_BACKGROUND_BYTES) {
      dispatch({ type: 'SET_ERROR', error: t('manager.modules.warehouseLayout.settings.backgroundTooLarge', {}, 'Plantegningen er for stor (maks 5 MB)') });
      return;
    }

    setBackgroundBusy(true);
    try {
      await uploadBackground(file);
    } catch (err) {
      console.error('Kunne ikke laste opp plantegning:', err);
      dispatch({ type: 'SET_ERROR', error: t('manager.modules.warehouseLayout.settings.backgroundUploadFailed', {}, 'Kunne ikke laste opp plantegning') });
    } finally {
      setBackgroundBusy(false);
    }
  };

  const handleRemoveBackground = async () => {
    setBackgroundBusy(true);
    try {
      await removeBackground();
    } catch (err) {
      console.error('Kunne ikke fjerne plantegning:', err);
    } finally {
      setBackgroundBusy(false);
    }
  };

  return (
    <div className="p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mb-3">{t('manager.modules.warehouseLayout.settings.title', {}, 'Varehus-dimensjoner')}</h3>
      <div className="space-y-2">
        <div>
          <label className="text-xs text-gray-600">{t('manager.modules.warehouseLayout.settings.length', {}, 'Lengde (m)')}</label>
          <input
            type="number"
            value={layout.dimensions.length}
            onChange={(e) => dispatch({ type: 'UPDATE_DIMENSIONS', field: 'length', value: Number(e.target.value) })}
            className="w-full px-2 py-1 text-sm border rounded"
            min={10}
            max={200}
          />
        </div>
        <div>
          <label className="text-xs text-gray-600">{t('manager.modules.warehouseLayout.settings.width', {}, 'Bredde (m)')}</label>
          <input
            type="number"
            value={layout.dimensions.width}
            onChange={(e) => dispatch({ type: 'UPDATE_DIMENSIONS', field: 'width', value: Number(e.target.value) })}
            className="w-full px-2 py-1 text-sm border rounded"
            min={10}
            max={200}
          />
        </div>
        <div>
          <label className="text-xs text-gray-600">{t('manager.modules.warehouseLayout.settings.height', {}, 'Hoyde (m)')}</label>
          <input
            type="number"
            value={layout.dimensions.height}
            onChange={(e) => dispatch({ type: 'UPDATE_DIMENSIONS', field: 'height', value: Number(e.target.value) })}
            className="w-full px-2 py-1 text-sm border rounded"
            min={3}
            max={20}
          />
        </div>
      </div>

      {/* Plantegning-underlag */}
      <h3 className="text-xs font-semibold uppercase tracking-wide text-gray-500 mt-5 mb-2 pt-4 border-t border-gray-200">
        {t('manager.modules.warehouseLayout.settings.backgroundTitle', {}, 'Plantegning (underlag)')}
      </h3>
      <p className="text-xs text-gray-500 mb-2">
        {t('manager.modules.warehouseLayout.settings.backgroundHint', {}, 'Bildet strekkes over gulvflaten — juster lengde/bredde over så tegningen får riktig målestokk.')}
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept="image/png,image/jpeg,image/webp"
        className="hidden"
        onChange={handleBackgroundFile}
      />
      <div className="flex gap-2">
        <button
          onClick={() => fileInputRef.current?.click()}
          disabled={backgroundBusy}
          className="flex-1 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
        >
          {backgroundBusy
            ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
            : <Upload className="w-3.5 h-3.5" />}
          {backgroundUrl
            ? t('manager.modules.warehouseLayout.settings.backgroundReplace', {}, 'Bytt plantegning')
            : t('manager.modules.warehouseLayout.settings.backgroundUpload', {}, 'Last opp plantegning')}
        </button>
        {backgroundUrl && (
          <button
            onClick={handleRemoveBackground}
            disabled={backgroundBusy}
            className="px-2 py-1.5 text-xs bg-red-100 text-red-700 rounded hover:bg-red-200 disabled:opacity-50"
            title={t('manager.modules.warehouseLayout.settings.backgroundRemove', {}, 'Fjern plantegning')}
          >
            <Trash2 className="w-3.5 h-3.5" />
          </button>
        )}
      </div>

      {/* To-punkts skala-kalibrering — kun relevant når plantegning finnes */}
      {backgroundUrl && (
        <>
          <button
            onClick={() => {
              // Kalibrering skjer i 2D-editoren — bytt visning om nødvendig
              dispatch({ type: 'SET_VIEW_MODE', mode: '2d' });
              dispatch({ type: 'START_CALIBRATION' });
            }}
            className="w-full mt-2 flex items-center justify-center gap-1.5 px-2 py-1.5 text-xs bg-amber-500 text-white rounded hover:bg-amber-600"
          >
            <Ruler className="w-3.5 h-3.5" />
            {t('manager.modules.warehouseLayout.calibration.start', {}, 'Kalibrer målestokk')}
          </button>
          <p className="text-xs text-gray-500 mt-1.5">
            {t('manager.modules.warehouseLayout.calibration.hint', {}, 'Klikk to punkter med kjent avstand på tegningen (f.eks. en vegg) og oppgi hvor langt det er i virkeligheten. NB: tegningen må være riktig orientert — rotasjon støttes ikke.')}
          </p>
        </>
      )}
    </div>
  );
};

export default SettingsPanel;
